import { useReducer, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { userAppContext, searchContext } from '../Context/Context';

// Components
import Home from '../Pages/Home';
import Support from '../Pages/Support';
import Privacy from '../Pages/PrivacyPolicy';
import CookiesPolicy from '../Pages/CookiesPolicy';
import Api from '../Pages/Api';
import ProfileArea from '../Pages/ProfileArea';
import NotFoundContent from '../Pages/NotFound';
import BadParamsContent from '../Pages/BadParams';
import About from '../Pages/About';
import LegalNotices from '../Pages/LegalNotices';
import AccountActivation from '../Pages/AccountActivation';
import AccountActivationError from '../Pages/AccountActivationError';
import Reset from '../Pages/Reset';
import Search from '../modules/search/Search';
import LoginPage from '../modules/login/pages/LoginPage';
import LogoutPage from '../modules/login/pages/LogoutPage';
import Login from '../modules/login/pages/LoginSso';
import SsoCallback from '../modules/login/pages/SsoCallback';
import Plans from '../Pages/Plans';
import ExpiredPage from '../Pages/ExpiredPage';
import ApplyTrialPage from '../Pages/ApplyTrialPage';
import ApplyAcademicPage from '../Pages/ApplyAcademicPage';
import ChatBotPage from '../Pages/ChatBotPage';
import BiomarkersPage from '../Pages/BiomarkersPage';
import AdvancedSearchPage from '../Pages/AdvancedSearchPage';
import UseProtectedRoute from './hooks/useProtectedRoute';
import LoadingSpinner from '../global/Loader/LoaderSpinner';
import AdvancedPDFViewer from '../Components/PdfViewer/PdfViewer';

// Hooks and reducers
import { useUrlSync } from './hooks/useUrlSync';
import { useSearchNavigation } from './hooks/useSearchNavigation';
import { useMappingContent } from './hooks/useMappingContent';
import { useRouteRedirects } from './hooks/useRouteRedirect';
import { useDataReadyState } from './hooks/useDataReadyState';
import { usePosts } from '../hooks/usePosts';
import { appRouteReducer, initialState } from './reducers/appRouteReducer';
import { SearchContextType, UserAppContextType } from './types';
import EnrichmentPage from '../Pages/Enrichment';
import PricingTablesAcademic from '../Components/Pricings/PricingTableAcademic';
import PaymentSuccess from '../Pages/PaymentSuccess';
import PaymentCancel from '../Pages/PaymentCancel';
import ThankYouPage from '../Pages/ThankYouPage';
import BookADemo from '../Pages/BookADemo';

const SearchWithDataReady = ({ dataReady, isLoading }: { dataReady: boolean; isLoading: boolean }) => {
  const showOverlay = isLoading || !dataReady;

  return (
    <div style={{ position: 'relative' }}>
      <Search />
      {showOverlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <LoadingSpinner message="Loading..." />
        </div>
      )}
    </div>
  );
};

const RedirectFAQ = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.location.href = 'https://support.disgenet.com/support/home';
  }, [navigate]);

  return null;
};

export const CustomRoutes = () => {
  const { posts } = usePosts();
  const [state, dispatch] = useReducer(appRouteReducer, initialState);
  const { isLoading, hasValidParams, isDataReady } = state;
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  // Context values with proper typing
  const searchContextValue = searchContext();
  const userAppContextValue = userAppContext();

  // Type guard to ensure contexts are not null
  if (!searchContextValue || !userAppContextValue) {
    return <LoadingSpinner message="Loading app..." />;
  }

  // Context values
  const {
    view,
    setView,
    source,
    setSource,
    tab,
    setTab,
    searchItems,
    setSearchItems,
    setHasValidParams,
    mappingContent,
    setMappingContent,
  } = searchContextValue as SearchContextType;

  const {
    authorization,
    isAuthenticated,
    setIsAuthenticated,
    setGroups,
    setAuthError,
    setAuthorization,
    restrictSources,
  } = userAppContextValue as UserAppContextType;

  // Custom hooks for managing different aspects of the component
  useUrlSync({
    searchParams,
    hasValidParams,
    restrictSources,
    isLoading,
    dispatch,
    setView,
    setSource,
    setTab,
    setSearchItems,
    setHasValidParams,
    setMappingContent,
    authorization,
    isAuthenticated,
    setAuthorization,
    setIsAuthenticated,
    setGroups,
    setAuthError,
    searchItems,
  });

  useSearchNavigation({
    searchItems,
    searchParams,
    isLoading,
  });

  useMappingContent({
    searchParams,
    view,
    tab,
    searchItems,
    setMappingContent,
  });

  useDataReadyState({
    searchItems,
    mappingContent,
    isLoading,
    view,
    source,
    tab,
    dispatch,
  });

  useRouteRedirects({
    posts: posts.map((post: any) => ({ postname: post.link })),
  });

  useEffect(() => {
    if (!location.pathname.includes['search']) localStorage.setItem('tableColumnOpen', 'false');
  }, [location]);

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/Support" element={<Support />} />
      <Route path="/Privacy-policy" element={<Privacy />} />
      <Route path="/Cookies-policy" element={<CookiesPolicy />} />
      <Route path="/FAQ" element={<RedirectFAQ />} />
      <Route path="/About" element={<About />} />
      <Route path="/Activate" element={<AccountActivation />} />
      <Route path="/ActivationError" element={<AccountActivationError />} />
      <Route path="/Legal" element={<LegalNotices />} />
      <Route path="/Profile-area" element={<ProfileArea />} />
      <Route path="/Tools" element={<Api />} />
      <Route path="/Reset" element={<Reset />} />

      {/* PDF viewer - no layout */}
      <Route
        path="/brochure"
        element={
          <AdvancedPDFViewer
            pdfUrl="../../../static/pdf/Brochure.pdf"
            logoUrl="../../../static/images/LogoDisgenet2.png"
            companyName="DISGENET"
          />
        }
      />

      {/* Thank you page for linkedin */}
      <Route path="/thank-you" element={<ThankYouPage />} />
      {/* Book a demo page with Calendly */}
      <Route path="/book-a-demo" element={<BookADemo />} />

      <Route
        path="/search"
        element={
          <UseProtectedRoute>
            <SearchWithDataReady dataReady={isDataReady} isLoading={isLoading} />
          </UseProtectedRoute>
        }
      />
      <Route
        path="/examples"
        element={<SearchWithDataReady dataReady={isDataReady} isLoading={isLoading} />}
      />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso" element={<Login />} />
      <Route path="/sso/callback" element={<SsoCallback />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/academic-plans" element={<PricingTablesAcademic />} />
      <Route path="/trial-expired" element={<ExpiredPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-cancel" element={<PaymentCancel />} />
      <Route path="/Error" element={<BadParamsContent />} />
      <Route path="/free-trial-apply" element={<ApplyTrialPage />} />
      <Route path="/academic-apply" element={<ApplyAcademicPage />} />
      <Route
        path="/Assistant"
        element={
          <UseProtectedRoute>
            <ChatBotPage />
          </UseProtectedRoute>
        }
      />
      <Route path="/biomarkers" element={<BiomarkersPage />} />
      <Route
        path="/advancedSearch"
        element={
          <UseProtectedRoute>
            <AdvancedSearchPage />
          </UseProtectedRoute>
        }
      />
      <Route
        path="/enrichment"
        element={
          <UseProtectedRoute>
            <EnrichmentPage />
          </UseProtectedRoute>
        }
      />
      <Route path="/*" element={<NotFoundContent />} />
    </Routes>
  );
};
